import {RootState} from 'app/rootReducer';
import {AppDispatch} from 'app/store';
import {
  Connection,
  CONNECTION_TYPE,
  IncomingMessage,
  LastMessage,
  Message,
  NormalizedConnections,
  NormalizedMessageResponse,
} from 'interfaces/Pages/Messaging';
import {MessagingService} from 'services/api';

import {CometChat} from '@cometchat-pro/chat';
import {AsyncThunk, createAction, createAsyncThunk} from '@reduxjs/toolkit';

const loginUserOnCometChat: AsyncThunk<
  CometChat.User | undefined,
  {
    authToken: string;
    cb?: () => void;
  },
  {
    state: RootState;
    rejectValue: string;
  }
> = createAsyncThunk<
  CometChat.User | undefined,
  {
    authToken: string;
    cb?: () => void;
  },
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>(
  'cometchat/loginUserOnCometChat',
  async ({authToken, cb}, {rejectWithValue}) => {
    try {
      const user = await CometChat.login(authToken);
      if (typeof cb === 'function') {
        cb();
      }
      return user;
    } catch (error) {
      if (error) {
        return rejectWithValue(error?.toString());
      } else {
        return rejectWithValue('Unknown error');
      }
    }
  },
);

const getConnections: AsyncThunk<
  NormalizedConnections,
  {
    /**
     * Whether to fetch the next page of friends.
     */
    type: CONNECTION_TYPE;
    /**
     * Set to true if you want to refresh everything.
     */
    page: number;
    limit: number;
    refresh?: boolean;
  },
  {
    state: RootState;
    rejectValue: string;
  }
> = createAsyncThunk<
  NormalizedConnections,
  {
    /**
     * Whether to fetch the next page of friends.
     */
    type: CONNECTION_TYPE;
    /**
     * Set to true if you want to refresh everything.
     */
    page: number;
    limit: number;
    refresh?: boolean;
  },
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('GetConnections', async (payload, {rejectWithValue}) => {
  try {
    const result = await MessagingService.getConnections(payload);
    return result;
  } catch (error) {
    if (error) {
      return rejectWithValue(error.toString());
    } else {
      return rejectWithValue('Unknown error');
    }
  }
});

const getAConnection: AsyncThunk<
  Connection,
  string,
  {
    state: RootState;
    rejectValue: string;
  }
> = createAsyncThunk<
  Connection,
  string,
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('GetAConnection', async (payload, {rejectWithValue}) => {
  try {
    const result = await MessagingService.getAConnection(payload);
    return result;
  } catch (error) {
    if (error) {
      return rejectWithValue(error.toString());
    } else {
      return rejectWithValue('Unknown error');
    }
  }
});

const resetExternalConnection = createAction('ResetEternalConnection');

const resetSearchConnection = createAction('ResetSearchConnection');
const searchConnection = createAction<{
  type: CONNECTION_TYPE.ALL;
  page: number;
  limit: number;
  keyword: string;
}>('SearchConnection');
const searchConnectionFailure = createAction('SearchConnectionFailure');
const searchConnectionSuccess = createAction<NormalizedConnections>(
  'SearchConnectionSuccess',
);
const getMessages: AsyncThunk<
  NormalizedMessageResponse,
  {
    conversationId: string;
    limit: number;
    lastMessageId: string | null;
    firstCall: boolean;
  },
  {
    state: RootState;
    rejectValue: string;
  }
> = createAsyncThunk<
  NormalizedMessageResponse,
  {
    conversationId: string;
    limit: number;
    lastMessageId: string | null;
    firstCall: boolean;
  },
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('GetMessages', async (payload, {rejectWithValue}) => {
  try {
    const res = await MessagingService.getMessages(payload);
    return res;
  } catch (error) {
    if (error) {
      return rejectWithValue(error.toString());
    } else {
      return rejectWithValue('Unknown error');
    }
  }
});

const getUnreadAllMessageCount: AsyncThunk<
  number,
  void,
  {
    state: RootState;
    /**
     * Rejected error string.
     */
    rejectValue: string;
  }
> = createAsyncThunk<
  number,
  void,
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('cometchat/getUnreadAllMessageCount', async (_, {rejectWithValue}) => {
  try {
    const res = await MessagingService.getUnreadAllMessageCount();
    return res;
  } catch (error) {
    if (error) {
      return rejectWithValue(error.toString());
    } else {
      return rejectWithValue('Unknown error');
    }
  }
});

const cometChatLogOut = createAction('CometChatLogOut');
const cometChatLogOutFailure = createAction('CometChatLogOutFailure');
const cometChatLogOutSuccess = createAction('CometChatLogOutSuccess');

const setUnreadMessageCount = createAction<{uid: string; value: number}>(
  'CometChatSetUnreadMessageCount',
);

const setLastMessage = createAction<{
  uid: string;
  /**
   * The last message.
   */
  data: LastMessage;
}>('CometChatSetLastMessage');

const setUserPresenceStatus = createAction<{
  uid: string;
  status: 'online' | 'offline';
  lastActiveAt: number;
}>('CometChatSetUserPresenceStatus');

const setUserTypingStatus = createAction<{
  uid: string;
  typing: boolean;
}>('CometChatSetUserTypingStatus');

const markSentMessageAsDelivered = createAction<{
  messageId: string;
  deliveredAt: number;
}>('MarkSentMessageAsDelivered');

const markReceivedMessageAsDelivered = createAction<{
  messageId: string;
  deliveredAt: number;
}>('MarkReceivedMessageAsDelivered');

const markSentMessageAsRead = createAction<{
  messageId: string;
  readAt: number;
}>('MarkSentMessageAsRead');

const markReceivedMessageAsRead = createAction<{
  messageId: string;
  readAt: number;
  senderId: string;
}>('MarkReceivedMessageAsRead');

const addIncomingMessage = createAction<{
  message: IncomingMessage;
  isChatting: boolean;
}>('AddIncomingMessage');

const addOptimisticMessage = createAction<Message>('AddOptimisticMessage');
const updateOptimisticMessage = createAction<Partial<Message>>(
  'UpdateOptimisticMessage',
);

const setParticipantId = createAction<string>('SetParticipantId');

export {
  addIncomingMessage,
  addOptimisticMessage,
  cometChatLogOut,
  cometChatLogOutFailure,
  cometChatLogOutSuccess,
  getAConnection,
  getConnections,
  getMessages,
  getUnreadAllMessageCount,
  loginUserOnCometChat,
  markReceivedMessageAsDelivered,
  markReceivedMessageAsRead,
  markSentMessageAsDelivered,
  markSentMessageAsRead,
  resetExternalConnection,
  resetSearchConnection,
  searchConnection,
  searchConnectionFailure,
  searchConnectionSuccess,
  setLastMessage,
  setParticipantId,
  setUnreadMessageCount,
  setUserPresenceStatus,
  setUserTypingStatus,
  updateOptimisticMessage,
};
